import { Pagination } from '@/components';
import { Column, Row } from '@/components/SettingsLayout';
import { TBuild, TypeOfBuild } from '@/types';
import { Text, Button, CheckBox, Icon, IconType } from '@/ui';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';

type CheckoutGridProps = {
  builds: TBuild[];
  totalCount?: number;
  page?: number;
  setPage?: (page: number) => void;
  perPage?: number;
  setPerPage?: (perPage: number) => void;
};

function CheckOutGridInFormContext({
  builds,
  totalCount,
  page,
  setPage,
  perPage,
  setPerPage,
}: CheckoutGridProps) {
  const { control } = useFormContext();

  return (
    <Column gapSize='16' fullWidth>
      <div className={styles.checkoutFlowsContainer}>
        <Controller
          name='checkoutFlows'
          control={control}
          render={({ field }) => (
            <div className={styles.checkoutFlowGrid}>
              {builds.map((build) => (
                <CheckoutGridCard build={build} key={build._id} field={field} withCheckBox={true} />
              ))}
            </div>
          )}
        />
      </div>
      {totalCount && totalCount > 10 && page && setPage && perPage && setPerPage && (
        <Row justify='justify-between' fullWidth>
          <Pagination
            page={page}
            totalPages={Math.ceil(totalCount / Number(perPage))}
            setPage={setPage}
            perPage={perPage.toString()}
            setPerPage={setPerPage}
            maxResults={totalCount}
          />
        </Row>
      )}
    </Column>
  );
}

export default CheckOutGridInFormContext;

type Props = {
  build: TBuild;
  withCheckBox?: boolean;
  field?: any;
  printBlock?: boolean;
  onClick?: () => void;
  buttonSettingsOnClick?: (id: string) => void;
  buttonActionOnClick?: (id: string) => void;
  onClone?: (id: string) => void;
  buttonActionText?: string;
  isManaged?: boolean;
  isInstalledPreset?: boolean;
  onUninstallPreset?: (id: string) => void;
};
export const CheckoutGridCard = ({
  build,
  withCheckBox = false,
  field,
  printBlock = false,
  buttonSettingsOnClick,
  buttonActionOnClick,
  buttonActionText,
  isManaged = false,
  onClick,
  onClone,
}: Props) => {
  const { t } = useTranslation();
  return (
    <div className={styles.checkoutCard} onClick={onClick}>
      <div className={styles.checkoutImg}>
        {withCheckBox && (
          <div className={styles.checkBox}>
            <CheckBox
              backgroundWhite
              key={build._id}
              id={String(build._id)}
              isChecked={field?.value?.includes(build._id)}
              onChange={() => {
                const newValue = field?.value?.includes(build._id)
                  ? field.value.filter((value: any) => value !== build._id)
                  : [...field.value, build._id];

                field.onChange(newValue);
              }}
            />
          </div>
        )}
        <img
          src={
            build.previewImage
              ? build.previewImage
              : printBlock
                ? '/print_block_icon_empty.png'
                : '/custom-flow-card.svg'
          }
          alt='cardImage'
        />
        {onClone ? (
          <button onClick={() => onClone(build._id)} className={styles.cloneButton}>
            <Icon type={IconType.Copy} width={26} height={26} fill='transparent' />
          </button>
        ) : null}
      </div>
      <Column gapSize='12' fullWidth>
        <Column gapSize='2' fullWidth>
          <Row fullWidth justify='justify-between'>
            <Text size='m' weight='light-medium' className='truncate'>
              {build.name}
            </Text>
            <Text size='xs' weight='light-medium' color='base-blue'>
              {build.buildType === TypeOfBuild.OLIVER_THEME ||
              build.buildType === TypeOfBuild.ORG_THEME
                ? t('Build.preset')
                : t('Build.custom')}
            </Text>
          </Row>
          <Row fullWidth justify='justify-between'>
            <Text size='xxs' weight='light-medium' color='base-grey'>
              {build.tag ? t(`Build.${build.tag}`) : ''}
            </Text>

            {build.version ? (
              <div className={styles.versionContainer}>
                <Text size='xxs' weight='light-medium'>
                  {build.version}
                </Text>
              </div>
            ) : (
              <Text size='xxs' weight='light-medium' color='base-blue'>
                {t('Build.draft')}
              </Text>
            )}
          </Row>
        </Column>
        {buttonSettingsOnClick || buttonActionOnClick ? (
          <Row fullWidth justify='justify-between' gapSize='8'>
            {buttonActionOnClick && (
              <Button
                onClick={() => buttonActionOnClick(build._id)}
                variant='secondary'
                style={{ width: '100%' }}
                children={buttonActionText || t('Build.open_builder')}
              ></Button>
            )}
            {buttonSettingsOnClick && !isManaged && (
              //had to add my own bc the existing did not match
              <button
                onClick={() => buttonSettingsOnClick(build._id)}
                className={styles.settingsButton}
              >
                <Icon
                  type={IconType.Settings}
                  width={24}
                  height={24}
                  stroke='#2797E8'
                  fill='transparent'
                />
              </button>
            )}
          </Row>
        ) : null}
      </Column>
    </div>
  );
};
